import { useEffect, useState, useCallback } from "react";
import { Plus, CheckCircle2, Circle, Trash2, Pencil, Phone, Mail, Linkedin, Users, Video, RotateCw, ListTodo, CalendarClock } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import FormDialog from "../components/FormDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import CommittenteSelect, { CommittenteBadge } from "../components/CommittenteSelect";
import { FilterSelect } from "./Companies";
import { Button } from "@/components/ui/button";
import { ACTIVITY_FIELDS, OPTIONS } from "../lib/entities";
import { label, fmtDateTime } from "../lib/format";

const TYPE_ICON = { telefonata: Phone, email: Mail, linkedin: Linkedin, meeting: Users, "video call": Video, "follow-up": RotateCw, task: ListTodo };
const VIEWS = [
  { id: "oggi", label: "Oggi" }, { id: "domani", label: "Domani" }, { id: "settimana", label: "Questa settimana" },
  { id: "scadute", label: "Scadute" }, { id: "future", label: "Future" }, { id: "completate", label: "Completate" },
];

export default function Activities() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(new URLSearchParams(window.location.search).get("view") || "oggi");
  const [committente, setCommittente] = useState("all");
  const [classification, setClassification] = useState("all");
  const [contactType, setContactType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [create, setCreate] = useState(false);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [followup, setFollowup] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { view };
    if (committente !== "all") params.committente = committente;
    if (classification !== "all") params.classification = classification;
    if (contactType !== "all") params.contact_type = contactType;
    const { data } = await api.get("/activities", { params });
    setRows(data); setLoading(false);
  }, [view, committente, classification, contactType]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const openId = new URLSearchParams(window.location.search).get("open");
    if (openId) api.get(`/activities/${openId}`).then(({ data }) => setEdit(data)).catch(() => toast.error("Attività non trovata"));
  }, []);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("crm-refresh", h);
    return () => window.removeEventListener("crm-refresh", h);
  }, [load]);

  const complete = async (a) => {
    await api.put(`/activities/${a.id}`, { status: "completata" });
    toast.success("Attività completata");
    load();
    setFollowup(a);
  };
  const remove = async () => { await api.delete(`/activities/${del.id}`); toast.success("Eliminata"); setDel(null); load(); };

  return (
    <div>
      <PageHeader title="Attività & Task" subtitle="Gestione operativa delle interazioni">
        {can("write") && <Button data-testid="add-activity-button" onClick={() => setCreate(true)} className="bg-navy-900 hover:bg-navy-700 text-white gap-1.5"><Plus className="w-4 h-4" /> Nuova Attività</Button>}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1.5 bg-white border border-slate-200 rounded-xl p-1.5 w-full sm:w-fit overflow-x-auto kanban-scroll">
          {VIEWS.map((v) => (
            <button key={v.id} data-testid={`activity-view-${v.id}`} onClick={() => setView(v.id)} className={`px-3.5 py-2 sm:py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${view === v.id ? "bg-navy-900 text-white font-medium" : "text-slate-600 hover:bg-slate-100"}`}>{v.label}</button>
          ))}
        </div>
        <CommittenteSelect mode="filter" testid="filter-activity-committente" value={committente} onChange={setCommittente} />
        <FilterSelect testid="filter-activity-classification" value={classification} onChange={setClassification} placeholder="Classif. azienda" options={OPTIONS.classification} />
        <FilterSelect testid="filter-activity-contact-type" value={contactType} onChange={setContactType} placeholder="Tipologia contatto" options={OPTIONS.contact_type} />
      </div>

      <div className="space-y-2">
        {loading ? <p className="text-slate-400 py-8 text-center">Caricamento…</p>
          : rows.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 py-14 text-center text-slate-400"><CalendarClock className="w-8 h-8 mx-auto mb-2 opacity-40" />Nessuna attività in questa vista</div>
          : rows.map((a) => {
            const Icon = TYPE_ICON[a.type] || ListTodo;
            const done = a.status === "completata";
            return (
              <div key={a.id} data-testid={`activity-item-${a.id}`} className="bg-white rounded-xl border border-slate-200 px-3 sm:px-4 py-3 flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2 hover:shadow-sm transition-shadow">
                <button data-testid={`complete-activity-${a.id}`} onClick={() => !done && complete(a)} className="shrink-0 icon-btn">
                  {done ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-300 hover:text-teal-500" />}
                </button>
                <div className="w-8 h-8 rounded-lg bg-slate-100 hidden sm:flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-slate-500" /></div>
                <div className="min-w-0 flex-1 basis-[calc(100%-7rem)] sm:basis-auto">
                  <p className={`font-medium truncate flex items-center gap-2 ${done ? "line-through text-slate-400" : "text-slate-800"}`}>{a.subject}<CommittenteBadge value={a.committente} testid={`activity-committente-${a.id}`} />
                    {a.teams_join_url && <a data-testid={`teams-link-${a.id}`} href={a.teams_join_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="Partecipa su Teams" className="inline-flex items-center gap-1 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-700 px-1.5 py-0.5 text-[10px] font-semibold"><Video className="w-3 h-3" /> Teams</a>}
                    {a.source === "Outlook" && <span data-testid={`outlook-badge-${a.id}`} className="inline-flex items-center rounded-md bg-sky-50 border border-sky-200 text-sky-700 px-1.5 py-0.5 text-[10px] font-semibold">Outlook</span>}
                  </p>
                  <p className="text-xs text-slate-400 truncate">{label("activity_type", a.type)} · {a.company?.legal_name || "Senza azienda"}{a.company?.classification && ` (${label("classification", a.company.classification)})`}{a.contact && ` · ${a.contact.first_name} ${a.contact.last_name}`}{a.contact?.contact_type && ` (${label("contact_type", a.contact.contact_type)})`} · {fmtDateTime(a.due_date)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-auto sm:ml-0 flex-wrap">
                  <StatusBadge group="priority" value={a.priority} />
                  {a.is_overdue ? <span data-testid={`overdue-badge-${a.id}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border bg-red-50 text-red-700 border-red-200">SCADUTA{a.overdue_notified_at && " · notificata"}</span> : <StatusBadge group="activity_status" value={a.status} />}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {can("write") && <button data-testid={`edit-activity-${a.id}`} onClick={() => setEdit(a)} className="icon-btn p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>}
                  {can("delete") && <button data-testid={`delete-activity-${a.id}`} onClick={() => setDel(a)} className="icon-btn p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
            );
          })}
      </div>

      <FormDialog open={create} onOpenChange={setCreate} title="Nuova Attività" fields={ACTIVITY_FIELDS} endpoint="/activities" onSaved={load} />
      {edit && <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title="Modifica Attività" fields={ACTIVITY_FIELDS} initial={edit} endpoint={`/activities/${edit.id}`} method="put" onSaved={load} />}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} onConfirm={remove} description={`Eliminare "${del?.subject}"?`} />
      {followup && (
        <FormDialog
          open={!!followup}
          onOpenChange={(o) => !o && setFollowup(null)}
          title="Programmare prossimo follow-up?"
          fields={ACTIVITY_FIELDS}
          initial={{ type: "follow-up", company_id: followup.company_id, contact_id: followup.contact_id, opportunity_id: followup.opportunity_id, subject: `Follow-up: ${followup.subject}` }}
          endpoint="/activities"
          onSaved={() => { setFollowup(null); load(); }}
        />
      )}
    </div>
  );
}
